import { useState } from 'preact/hooks';
import { Spinner } from '@components/spinner';
import { StripeContext, StripeInput } from '../checkout/stripe';
import { IcoLock } from '@components/icons';
import { StripeAddress } from 'shared/payments/types';
import { describeCheckoutError, loggableCheckoutError } from '../checkout/checkout-logic';
import { reportCheckoutError } from '@course/components/checkout/report-checkout-error';

/**
 * Turn the card the buyer typed into the Payment Element into an actual
 * payment method, with billing name + address read from the Address Element.
 * Required because the Payment Element opts out of billing-details fields
 * (StripeInput sets them to 'never') and the accept endpoint otherwise falls
 * back to a saved card that doesn't exist for these buyers. Shared by the
 * custom upsell page CTA and the plain post-purchase OfferView.
 */
export async function collectUpsellPaymentMethod(
  stripeCtx: StripeContext,
): Promise<{ paymentMethodId: string; address?: StripeAddress } | { error: string }> {
  try {
    const addressElement = stripeCtx.elements.getElement('address');
    const submitResult = await stripeCtx.elements.submit();
    if (submitResult.error && submitResult.error.type !== 'validation_error') {
      throw submitResult.error;
    }
    if (submitResult.error) {
      // The Payment Element renders its own field-level validation.
      console.warn('upsell submit validation', loggableCheckoutError(submitResult.error));
      return { error: '' };
    }

    let address: StripeAddress | undefined;
    let billingName = '';
    if (addressElement) {
      const addressFormState = await addressElement.getValue();
      if (!addressFormState.complete) {
        return { error: 'Please complete your billing address.' };
      }
      billingName = addressFormState.value.name || '';
      address = {
        ...addressFormState.value.address,
        line2: addressFormState.value.address?.line2 || undefined,
      };
    }

    const pmResult = await stripeCtx.stripe.createPaymentMethod({
      elements: stripeCtx.elements,
      params: {
        billing_details: {
          name: billingName,
          address: address || { country: 'US' },
        },
      },
    });
    if (pmResult.error) {
      throw pmResult.error;
    }

    return { paymentMethodId: pmResult.paymentMethod.id, address };
  } catch (err) {
    console.warn('upsell card error', loggableCheckoutError(err));
    reportCheckoutError({ step: 'upsell', err });
    return { error: describeCheckoutError(err).message };
  }
}

type Props = {
  acceptText: string;
  declineText: string;
  onAccept(paymentMethodId?: string, address?: StripeAddress): void;
  onDecline(): void;
  isProcessing: boolean;
  cardInfo?: { last4: string; brand: string };
  priceId?: string;
  trialDays?: number;
  priceDisplay?: string;
  amount?: number;
  currency?: string;
  paymentType?: string;
};

export function UpsellCTA({
  acceptText,
  declineText,
  onAccept,
  onDecline,
  isProcessing,
  cardInfo,
  priceId,
  trialDays,
  priceDisplay,
  amount,
  currency,
  paymentType,
}: Props) {
  const [stripeCtx, setStripeCtx] = useState<StripeContext | undefined>(undefined);
  const [cardError, setCardError] = useState<string | undefined>(undefined);
  const hasCard = !!cardInfo;

  const handleAcceptWithCard = async () => {
    if (!stripeCtx) {
      return;
    }
    setCardError(undefined);
    const result = await collectUpsellPaymentMethod(stripeCtx);
    if ('error' in result) {
      result.error && setCardError(result.error);
      return;
    }
    onAccept(result.paymentMethodId, result.address);
  };

  return (
    <div class="text-center">
      {trialDays && priceDisplay ? (
        <p class="text-gray-600 text-sm mb-4 font-medium">
          $0 today — then {priceDisplay} after your {trialDays}-day free trial
        </p>
      ) : null}

      {!hasCard && priceId && (
        <div class="max-w-sm mx-auto mb-6 text-left">
          <StripeInput
            customerName=""
            priceId={priceId}
            onReady={setStripeCtx}
            collectAddress={true}
            manualPaymentMethod={true}
            disabled={isProcessing}
            amount={amount}
            currency={currency}
            mode={
              trialDays
                ? 'setup'
                : paymentType === 'subscription' || paymentType === 'paymentplan'
                  ? 'subscription'
                  : 'payment'
            }
          />
          {cardError && (
            <div
              role="alert"
              class="mt-3 rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700"
            >
              {cardError}
            </div>
          )}
        </div>
      )}

      <button
        type="button"
        class="font-bold py-4 px-10 rounded-lg text-lg transition-all disabled:opacity-50"
        style="background: linear-gradient(135deg, var(--m-orange-cta-start, #ff6b00) 0%, var(--m-orange-cta-end, #ffc600) 100%); color: var(--m-text-dark, #1a1a1a); box-shadow: 0 2px 8px rgba(255,107,0,0.3)"
        onClick={() => {
          if (hasCard) {
            onAccept();
          } else {
            handleAcceptWithCard();
          }
        }}
        disabled={isProcessing || (!hasCard && !stripeCtx)}
      >
        {isProcessing ? <Spinner /> : acceptText}
      </button>

      <button
        type="button"
        class="block mx-auto mt-4 text-gray-500 text-sm underline hover:text-gray-700 cursor-pointer"
        onClick={onDecline}
        disabled={isProcessing}
      >
        {declineText}
      </button>

      {hasCard && (
        <div class="flex items-center justify-center gap-2 mt-4">
          <span class="inline-flex items-center gap-1.5 text-xs font-medium text-gray-500 bg-white border border-gray-200 rounded-full px-3 py-1">
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <rect width="20" height="14" x="2" y="5" rx="2" />
              <line x1="2" y1="10" x2="22" y2="10" />
            </svg>
            {cardInfo.brand.charAt(0).toUpperCase() + cardInfo.brand.slice(1)} ···· {cardInfo.last4}
          </span>
        </div>
      )}

      <p class="flex items-center justify-center gap-1.5 text-xs text-gray-400 mt-4">
        <IcoLock class="size-3 text-gray-400" />
        Secure checkout powered by Stripe
      </p>
    </div>
  );
}
